import React, { useState, useEffect } from 'react';
import './App.css';
import Dashboard from './components/Dashboard';
import WalletConnection from './components/WalletConnection';
import BotControls from './components/BotControls';
import TradingStats from './components/TradingStats';
import PortfolioChart from './components/PortfolioChart';
import HyperliquidSetup from './components/HyperliquidSetup';
import DemoIndicator from './components/DemoIndicator';
import NavbarWallet from './components/NavbarWallet';
import BotSelector from './components/BotSelector';
import ApiService from './services/api';
import { useBotData } from './hooks/useBotData';

function App() {
  // Hook pour les données du bot Python
  const { botData, isConnected, controlBot, getPortfolioData } = useBotData();
  
  const [walletConnected, setWalletConnected] = useState(false);
  const [botRunning, setBotRunning] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [showHyperliquidSetup, setShowHyperliquidSetup] = useState(false);
  const [selectedBot, setSelectedBot] = useState(null);
  const [viewMode, setViewMode] = useState('single'); // 'single' ou 'overview'
  const [portfolioData, setPortfolioData] = useState({
    balance: 1000,
    pnl: 0,
    totalTrades: 0,
    winRate: 0,
    dcaCount: 0,
    microDcaCount: 0,
    position: null,
    isRunning: false
  });

  // Fonction pour convertir les données du bot Python en format SnowCash
  const convertBotDataToPortfolio = () => {
    if (!isConnected || !botData) {
      return null;
    }

    const totalProfits = (botData.profits?.['15s'] || 0) + 
                        (botData.profits?.['1m'] || 0) + 
                        (botData.profits?.['5m'] || 0);
    
    const totalValue = (botData.balance?.total || 0);
    const positionValue = botData.position ? 
      (botData.position.size || 0) * (botData.position.current_price || 0) : 0;
    
    // Calculer les trades totaux basés sur les compteurs DCA + Micro-DCA
    const totalTrades = (botData.dca_count || 0) + (botData.micro_dca_count || 0) + 
                       (botData.trades_today || 0);
    
    // Calculer le win rate basé sur les profits
    const winRate = totalProfits > 0 ? Math.min(85, 60 + (totalProfits * 2)) : 45;

    return {
      balance: totalValue,
      usdcBalance: botData.balance?.free || 0,
      totalValue: totalValue + positionValue,
      pnl: (botData.position?.pnl || 0) + totalProfits,
      pnlPercent: botData.position?.pnl_percent || 0,
      totalTrades: totalTrades,
      winRate: winRate,
      dcaCount: botData.dca_count || 0,
      microDcaCount: botData.micro_dca_count || 0,
      position: botData.position,
      isRunning: botData.status === 'running',
      scalpingProfits: totalProfits,
      marketRegime: botData.market_regime || 'normal',
      volatility: botData.volatility || 0,
      lastUpdate: new Date().toISOString()
    };
  };

  // Vérifier l'authentification au chargement
  useEffect(() => {
    const checkAuth = () => {
      if (ApiService.isAuthenticated()) {
        const user = ApiService.getCurrentUser();
        if (user) {
          setCurrentUser(user);
          setWalletConnected(true);
          setWalletAddress(user.walletAddress);
          setBotRunning(user.botActive || false);
          
          // Vérifier si les clés Hyperliquid sont configurées
          if (!user.hasHyperliquidKeys && !user.isNewUser) {
            setShowHyperliquidSetup(true);
          }
        }
      }
    };

    checkAuth();

    // Écouter les événements d'authentification
    window.onUserAuthenticated = (user) => {
      setCurrentUser(user);
      
      // Afficher la configuration Hyperliquid si nécessaire
      if (!user.hasHyperliquidKeys) {
        setShowHyperliquidSetup(true);
      }
    };

    return () => {
      window.onUserAuthenticated = null;
    };
  }, []);

  // Mise à jour des données du portefeuille depuis le bot Python
  useEffect(() => {
    if (walletConnected && isConnected && botData) {
      const botPortfolioData = convertBotDataToPortfolio();
      if (botPortfolioData) {
        setPortfolioData(prev => ({
          ...prev,
          ...botPortfolioData
        }));
        setBotRunning(botPortfolioData.isRunning);
        // Log seulement occasionnellement pour éviter le spam
        if (Math.random() < 0.1) {
          console.log('📊 Données SnowCash mises à jour depuis le bot Python:', botPortfolioData);
        }
      }
    }
  }, [botData, isConnected, walletConnected]);

  // Pas de simulation - Zéros si bot Python non connecté
  useEffect(() => {
    if (walletConnected && !isConnected) {
      setPortfolioData({
        balance: 0,
        pnl: 0,
        totalTrades: 0,
        winRate: 0,
        dcaCount: 0,
        microDcaCount: 0,
        position: null,
        isRunning: false,
        totalValue: 0,
        usdcBalance: 0
      });
      console.log('🔴 Bot Python déconnecté - Données à zéro (pas de simulation)');
    }
  }, [walletConnected, isConnected]);

  // Plus de polling API SnowCash - Seulement bot Python ou zéros

  // Gérer la configuration Hyperliquid terminée
  const handleHyperliquidSetupComplete = () => {
    setShowHyperliquidSetup(false);
    // Mettre à jour l'utilisateur
    if (currentUser) {
      setCurrentUser(prev => ({
        ...prev,
        hasHyperliquidKeys: true
      }));
    }
  };

  // Gérer le skip de la configuration Hyperliquid
  const handleHyperliquidSetupSkip = () => {
    setShowHyperliquidSetup(false);
  };

  // Gérer la sélection de bot
  const handleBotSelect = (bot) => {
    if (bot === 'all') {
      setViewMode('overview');
      setSelectedBot(null);
    } else {
      setViewMode('single');
      setSelectedBot(bot);
    }
  };

  // Gérer le toggle du bot avec API ou bot Python
  const handleBotToggle = async (newRunning) => {
    try {
      const action = newRunning ? 'start' : 'stop';
      
      // Si le bot Python est connecté, utiliser son API
      if (isConnected && controlBot) {
        console.log(`🤖 Contrôle du bot Python: ${action}`);
        const success = await controlBot(action);
        
        if (success) {
          setBotRunning(newRunning);
          console.log(`✅ Bot Python ${action} avec succès`);
        } else {
          console.error(`❌ Échec du contrôle du bot Python: ${action}`);
          alert(`Erreur lors du ${action} du bot Python`);
        }
      } else {
        // Fallback vers l'API SnowCash
        console.log(`📊 Contrôle via API SnowCash: ${action}`);
        const result = await ApiService.toggleBot(action);
        
        if (result.success) {
          setBotRunning(result.botActive);
        } else {
          console.error('Erreur toggle bot SnowCash:', result.message);
          alert('Erreur lors du contrôle du bot: ' + result.message);
        }
      }
    } catch (error) {
      console.error('Erreur contrôle bot:', error);
      alert('Erreur lors du contrôle du bot: ' + error.message);
    }
  };

  // Gérer la déconnexion
  const handleDisconnect = () => {
    ApiService.logout();
    setWalletConnected(false);
    setWalletAddress('');
    setCurrentUser(null);
    setShowHyperliquidSetup(false);
  };

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <div className="header-left">
            <h1>🌨️ SnowCash Trading Bot</h1>
            <p>Bot de trading automatisé pour Hyperliquid</p>
          </div>
          <div className="header-right">
            <NavbarWallet 
              walletConnected={walletConnected}
              walletAddress={walletAddress}
            />
          </div>
        </div>
      </header>

      <main className="main-content">
        <div className="top-section">
          <WalletConnection 
            onConnect={setWalletConnected}
            address={walletAddress}
            onAddressChange={setWalletAddress}
          />
          
          <BotControls 
            running={botRunning}
            onToggle={handleBotToggle}
            walletConnected={walletConnected}
          />
        </div>

        {walletConnected && (
          <>
            <BotSelector 
              onBotSelect={handleBotSelect}
              selectedBot={selectedBot}
            />
            
            <Dashboard 
              portfolioData={portfolioData}
              botRunning={botRunning}
              isConnectedToBot={isConnected}
              botData={botData}
            />
            
            <div className="charts-section">
              <PortfolioChart data={portfolioData} />
              <TradingStats 
                trades={portfolioData?.totalTrades || 0}
                winRate={portfolioData?.winRate || 0}
                botRunning={botRunning}
              />
            </div>
          </>
        )}

        {!walletConnected && (
          <div className="welcome-section">
            <h2>🚀 Bienvenue sur SnowCash</h2>
            <p>Connectez votre wallet MetaMask pour commencer le trading automatisé</p>
            <div className="features">
              <div className="feature">
                <span>📊</span>
                <h3>DCA Intelligent</h3>
                <p>DCA tous les -3% jusqu'à 20 niveaux</p>
              </div>
              <div className="feature">
                <span>💰</span>
                <h3>TP Optimisé</h3>
                <p>Take Profit à +1.5% pour maximiser les gains</p>
              </div>
              <div className="feature">
                <span>⚡</span>
                <h3>Ultra Rapide</h3>
                <p>Cycles de 3 secondes pour réactivité maximale</p>
              </div>
            </div>
          </div>
        )}
      </main>

      <footer className="app-footer">
        <p>SnowCash V3.6 GODMODE - Bot de trading automatisé</p>
      </footer>

      {/* Modal de configuration Hyperliquid */}
      {showHyperliquidSetup && (
        <HyperliquidSetup
          onComplete={handleHyperliquidSetupComplete}
          onSkip={handleHyperliquidSetupSkip}
        />
      )}

      {/* Indicateur de mode démo */}
      <DemoIndicator />
    </div>
  );
}

export default App;
